import { array, boolean, enum as enumFactory, int, literal, strictObject, string } from "../../lazy-zod.js";
import { openMeldRequestContextSchema } from "./profile-identity.js";
import { daemonServiceLaneSchema } from "./service-contract.js";
import { peopleComputerTargetSchema } from "../http/people-computers.js";
//#region src/schemas/daemon/service-connection-evidence.ts
const timestamp = int().nonnegative();
const identifier = string().regex(/^[A-Za-z0-9_-]{1,128}$/u);
const serviceConnectionEvidenceEventSchema = strictObject({
	kind: enumFactory([
		"connecting",
		"transport_open",
		"registered",
		"disconnected",
		"retry_scheduled",
		"credential_cache",
		"reply_reconsideration"
	]),
	connectionId: string().uuid(),
	firstAtMs: timestamp,
	lastAtMs: timestamp,
	count: int().min(1).max(1e7),
	phase: enumFactory([
		"credentials",
		"opening",
		"registering",
		"online",
		"waiting",
		"stopping"
	]),
	reason: enumFactory([
		"auth_unavailable",
		"auth_rejected",
		"auth_expired",
		"auth_missing",
		"network_unreachable",
		"open_timeout",
		"socket_closed",
		"heartbeat_timeout",
		"context_changed",
		"credential_changed",
		"account_changed",
		"metadata_unavailable",
		"credential_available",
		"space_changed",
		"recovered",
		"stopped",
		"unknown"
	]),
	durationMs: timestamp.optional(),
	retryAtMs: timestamp.optional(),
	lastHeartbeatAckAtMs: timestamp.optional(),
	closeCode: int().min(1e3).max(4999).optional(),
	nativeErrorCode: enumFactory([
		"ECONNRESET",
		"ECONNREFUSED",
		"ETIMEDOUT",
		"ENOTFOUND",
		"EAI_AGAIN",
		"ENETUNREACH",
		"EHOSTUNREACH",
		"EPIPE",
		"CERT_HAS_EXPIRED",
		"UNABLE_TO_VERIFY_LEAF_SIGNATURE",
		"DEPTH_ZERO_SELF_SIGNED_CERT"
	]).optional(),
	requestId: identifier.optional(),
	eventLoopDelayMs: timestamp.optional()
});
const serviceConnectionIncidentSchema = strictObject({
	v: literal(1),
	incidentId: string().uuid(),
	deviceId: identifier,
	runtimeLane: daemonServiceLaneSchema,
	serviceSessionId: string().uuid(),
	serviceVersion: string().regex(/^[0-9][0-9A-Za-z.+-]{0,63}$/u),
	startedAtMs: timestamp,
	endedAtMs: timestamp,
	outcome: enumFactory(["recovered", "interrupted"]),
	evidenceTruncated: boolean(),
	events: array(serviceConnectionEvidenceEventSchema).min(1).max(100)
}).refine((value) => value.endedAtMs >= value.startedAtMs, { message: "Invalid incident interval." });
const serviceConnectionIncidentReportSchema = strictObject({
	uploaderServiceSessionId: string().uuid(),
	incident: serviceConnectionIncidentSchema
});
const serviceConnectionIncidentReceiptSchema = strictObject({
	incidentId: string().uuid(),
	receivedAtMs: timestamp
});
const serviceConnectionIncidentsReadRpcInputSchema = peopleComputerTargetSchema.extend({ context: openMeldRequestContextSchema });
const serviceConnectionIncidentReportRpcInputSchema = serviceConnectionIncidentsReadRpcInputSchema.extend({ report: serviceConnectionIncidentReportSchema });
const serviceConnectionIncidentsResponseSchema = strictObject({ incidents: array(serviceConnectionIncidentSchema.safeExtend({ receivedAtMs: timestamp })).max(20) });
const serviceConnectionEvidenceJournalSchema = strictObject({
	v: literal(1),
	entries: array(strictObject({
		incident: serviceConnectionIncidentSchema,
		uploadedAtMs: timestamp.nullable()
	})).max(32)
});
const serviceConnectionTraceDiagnosticsSchema = strictObject({
	status: enumFactory(["available", "unavailable"]),
	incidents: array(strictObject({
		incidentId: string().uuid(),
		deviceId: identifier,
		runtimeLane: daemonServiceLaneSchema,
		startedAtMs: timestamp,
		endedAtMs: timestamp,
		outcome: enumFactory(["recovered", "interrupted"])
	})).max(20)
});
//#endregion
export { serviceConnectionEvidenceEventSchema, serviceConnectionEvidenceJournalSchema, serviceConnectionIncidentReceiptSchema, serviceConnectionIncidentReportRpcInputSchema, serviceConnectionIncidentReportSchema, serviceConnectionIncidentSchema, serviceConnectionIncidentsReadRpcInputSchema, serviceConnectionIncidentsResponseSchema, serviceConnectionTraceDiagnosticsSchema };

//# sourceMappingURL=service-connection-evidence.js.map